<!--
  Two stages, both local state (myWealth §2.7): pick a type, then fill that type's own
  form. `initialType` skips the picker entirely - the "Sell car" action uses this so
  selling stays its own deliberate act, never a status checkbox on an edit form.
-->
<script setup lang="ts">
import type {
  Car,
  CarEvent,
  CarEventData,
  CarEventJournal,
  CarEventType,
  InsurancePolicy,
  MaintenanceSchedule,
  RecordCarEventInput,
  UpdateCarEventInput,
  UpdateCarEventJournal,
} from '../internal/types.ts'
import {
  formatLocalDate,
  type Money,
  parseLocalDate,
  requireCurrencyCode,
  systemClock,
  todayUtc,
} from '@taler/domain'
import { ActionButton, SearchableSelect, Select, StatusTag } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import type { AccountOption } from '../internal/accounts.ts'
import { carAccountCodeFor } from '../internal/car-accounts.ts'
import { readDefaultAccountId, rememberDefaultAccountId } from '../internal/default-accounts.ts'
import { counterpartyFor } from '../internal/derive.ts'
import type { RecentEntryOption } from '../internal/entries.ts'
import EventTypeFields from '@/features/garage/components/EventTypeFields.vue'
import EventTypePicker from '@/features/garage/components/EventTypePicker.vue'

const props = defineProps<{
  car: Car
  ledgerId: string | undefined
  initialType?: CarEventType
  editingEvent?: CarEvent
  policies: readonly InsurancePolicy[]
  schedules: readonly MaintenanceSchedule[]
  expenseAccounts: readonly AccountOption[]
  sourceAccounts: readonly AccountOption[]
  incomeAccounts: readonly AccountOption[]
  recentEntries: readonly RecentEntryOption[]
  isSubmitting: boolean
  errorMessage: string | undefined
}>()
const emit = defineEmits<{
  submit: [RecordCarEventInput]
  update: [eventId: string, input: UpdateCarEventInput]
  cancel: []
}>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const isEditing = computed(() => props.editingEvent !== undefined)
const selectedType = ref<CarEventType | undefined>(props.editingEvent?.type ?? props.initialType)
const date = ref(
  props.editingEvent === undefined
    ? formatLocalDate(todayUtc(systemClock))
    : formatLocalDate(props.editingEvent.date),
)
const mileageKm = ref(props.editingEvent?.mileageKm === undefined ? '' : String(props.editingEvent.mileageKm))
const notes = ref(props.editingEvent?.notes ?? '')
const eventData = ref<CarEventData>(props.editingEvent?.data ?? { type: 'MILEAGE_LOG' })

type JournalMode = 'NONE' | 'CREATE' | 'LINK' | 'UNCHANGED'
const journalMode = ref<JournalMode>(isEditing.value ? 'UNCHANGED' : 'NONE')
function defaultAccountFor(type: CarEventType | undefined): string | undefined {
  if (type === undefined || props.ledgerId === undefined) return undefined
  const code = carAccountCodeFor(type)
  return (
    readDefaultAccountId(props.ledgerId, type) ??
    props.expenseAccounts.find((account) => account.code === code)?.id
  )
}
const expenseAccountId = ref<string | undefined>(
  isEditing.value ? undefined : defaultAccountFor(selectedType.value),
)
const sourceAccountId = ref<string | undefined>(props.editingEvent?.sourceAccountId)
const amount = ref<Money | null>(null)
const payee = ref('')
const payeeTouched = ref(false)
const entryId = ref<string | undefined>(undefined)
const validationError = ref<string | undefined>(undefined)

const showJournal = computed(() => selectedType.value !== undefined && selectedType.value !== 'MILEAGE_LOG')
const categoryAccounts = computed(() =>
  selectedType.value === 'SELL' ? props.incomeAccounts : props.expenseAccounts,
)
const categoryLabel = computed(() =>
  selectedType.value === 'SELL' ? t('garage.journal.incomeAccount') : t('garage.journal.expenseAccount'),
)
const categoryPlaceholder = computed(() =>
  selectedType.value === 'SELL'
    ? t('garage.journal.selectIncomeAccount')
    : t('garage.journal.selectExpenseAccount'),
)

// Derives the payee from whichever counterparty field the current event type carries -
// station, shop, authority, an insurer - unless the user has typed their own.
const derivedPayee = computed(() => counterpartyFor(eventData.value, props.policies))
watch(
  derivedPayee,
  (value) => {
    if (payeeTouched.value) return
    payee.value = value ?? ''
  },
  { immediate: true },
)

watch(selectedType, (type) => {
  journalMode.value = isEditing.value ? 'UNCHANGED' : 'NONE'
  expenseAccountId.value = defaultAccountFor(type)
  sourceAccountId.value = undefined
  amount.value = null
  payeeTouched.value = false
  entryId.value = undefined
})

// Editing starts in UNCHANGED mode with no category account of its own (AGENTS.md: a
// journal `CREATE` here is a brand-new entry, not a re-derivation of the recorded one);
// picking CREATE afterward still deserves the same default a fresh event gets.
watch(journalMode, (mode) => {
  if (mode !== 'CREATE' || expenseAccountId.value !== undefined) return
  expenseAccountId.value = defaultAccountFor(selectedType.value)
})

const journalModeOptions = computed(() =>
  isEditing.value
    ? [
        { value: 'UNCHANGED' as const, label: t('garage.journal.unchanged') },
        { value: 'CREATE' as const, label: t('garage.journal.create') },
        { value: 'LINK' as const, label: t('garage.journal.link') },
      ]
    : [
        { value: 'NONE' as const, label: t('garage.journal.none') },
        { value: 'CREATE' as const, label: t('garage.journal.create') },
        { value: 'LINK' as const, label: t('garage.journal.link') },
      ],
)

const entryOptions = computed(() =>
  props.recentEntries.map((entry) => ({ value: entry.id, label: entry.label })),
)
const categoryAccountOptions = computed(() =>
  categoryAccounts.value.map((account) => ({ value: account.id, label: account.label })),
)
const sourceAccountOptions = computed(() =>
  props.sourceAccounts.map((account) => ({ value: account.id, label: account.label })),
)
const journalSourceAccount = computed(() =>
  props.sourceAccounts.find((account) => account.id === sourceAccountId.value),
)
const journalSourceCurrency = computed(() =>
  journalSourceAccount.value?.currency === undefined
    ? undefined
    : requireCurrencyCode(journalSourceAccount.value.currency),
)

type BuiltJournal =
  | { mode: 'NONE' }
  | { mode: 'UNCHANGED' }
  | {
      mode: 'CREATE'
      expenseAccountId: string
      sourceAccountId: string
      amount: Money
      payee: string | undefined
    }
  | { mode: 'LINK'; entryId: string }

function buildJournal(): BuiltJournal | undefined {
  if (journalMode.value === 'UNCHANGED') return { mode: 'UNCHANGED' }
  if (!showJournal.value || journalMode.value === 'NONE') return { mode: 'NONE' }
  if (journalMode.value === 'LINK') {
    if (entryId.value === undefined) return undefined
    return { mode: 'LINK', entryId: entryId.value }
  }
  const sourceAccount = props.sourceAccounts.find((account) => account.id === sourceAccountId.value)
  if (
    expenseAccountId.value === undefined ||
    sourceAccountId.value === undefined ||
    sourceAccount?.currency === undefined
  ) {
    return undefined
  }
  if (amount.value === null) return undefined
  try {
    return {
      mode: 'CREATE',
      expenseAccountId: expenseAccountId.value,
      sourceAccountId: sourceAccountId.value,
      amount: amount.value,
      payee: payee.value.trim() === '' ? undefined : payee.value.trim(),
    }
  } catch {
    return undefined
  }
}

function submit(): void {
  validationError.value = undefined
  if (selectedType.value === undefined || date.value === '') return
  const journal = buildJournal()
  if (journal === undefined) {
    validationError.value = t('garage.form.invalidAmount')
    return
  }
  if (journal.mode === 'CREATE' && props.ledgerId !== undefined) {
    rememberDefaultAccountId(props.ledgerId, selectedType.value, journal.expenseAccountId)
  }
  if (isEditing.value && props.editingEvent !== undefined) {
    const input: UpdateCarEventInput = {
      type: selectedType.value,
      date: parseLocalDate(date.value),
      data: eventData.value,
      journal: journal as UpdateCarEventJournal,
      ...(mileageKm.value === '' ? {} : { mileageKm: parseInt(mileageKm.value, 10) }),
      ...(notes.value.trim() === '' ? {} : { notes: notes.value.trim() }),
    }
    emit('update', props.editingEvent.id, input)
    return
  }
  const input: RecordCarEventInput = {
    type: selectedType.value,
    date: parseLocalDate(date.value),
    data: eventData.value,
    journal: journal as CarEventJournal,
    ...(mileageKm.value === '' ? {} : { mileageKm: parseInt(mileageKm.value, 10) }),
    ...(notes.value.trim() === '' ? {} : { notes: notes.value.trim() }),
  }
  emit('submit', input)
}

const combinedError = computed(() => validationError.value ?? props.errorMessage)
</script>

<template>
  <EventTypePicker v-if="selectedType === undefined" @select="selectedType = $event" />

  <form v-else class="flex flex-col gap-4" @submit.prevent="submit">
    <div class="flex items-center gap-2">
      <button
        v-if="initialType === undefined && editingEvent === undefined"
        type="button"
        class="text-[color:var(--color-text-muted)]"
        :aria-label="t('garage.events.typeTitle')"
        @click="selectedType = undefined"
      >
        ←
      </button>
      <h3 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t(`garage.eventType.${selectedType}`) }}
      </h3>
    </div>

    <div class="grid grid-cols-2 gap-[var(--space-cell)]">
      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.eventField.date')
        }}</span>
        <input
          v-model="date"
          type="date"
          required
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.eventField.mileage')
        }}</span>
        <input
          v-model="mileageKm"
          type="number"
          min="0"
          placeholder="pl. 85000"
          class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>
    </div>

    <EventTypeFields
      v-model="eventData"
      :type="selectedType"
      :car="car"
      :policies="policies"
      :schedules="schedules"
      :is-editing="isEditing"
    />

    <label class="flex flex-col gap-1">
      <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
        t('garage.eventField.notes')
      }}</span>
      <textarea
        v-model="notes"
        rows="2"
        class="rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] py-2 text-[color:var(--color-text)]"
      />
    </label>

    <section v-if="showJournal" class="flex flex-col gap-3 border-t border-[color:var(--color-border)] pt-4">
      <h4 class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]">
        {{ t('garage.journal.title') }}
      </h4>
      <div class="flex gap-1" role="radiogroup" :aria-label="t('garage.journal.title')">
        <button
          v-for="option in journalModeOptions"
          :key="option.value"
          type="button"
          role="radio"
          :aria-checked="journalMode === option.value"
          class="rounded-[var(--radius-control)] border px-3 py-1.5 text-[length:var(--text-step-2)]"
          :class="
            journalMode === option.value
              ? 'border-[color:var(--color-accent)] bg-[color:var(--color-surface-raised)] text-[color:var(--color-text)]'
              : 'border-[color:var(--color-border-control)] text-[color:var(--color-text-muted)]'
          "
          @click="journalMode = option.value"
        >
          {{ option.label }}
        </button>
      </div>

      <div v-if="journalMode === 'CREATE'" class="grid grid-cols-2 gap-[var(--space-cell)]">
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            categoryLabel
          }}</span>
          <Select
            v-model="expenseAccountId"
            :options="categoryAccountOptions"
            :placeholder="categoryPlaceholder"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('garage.journal.sourceAccount')
          }}</span>
          <Select
            v-model="sourceAccountId"
            :options="sourceAccountOptions"
            :placeholder="t('garage.journal.selectSourceAccount')"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('garage.journal.amount')
          }}</span>
          <AmountInput v-model="amount" :currency="journalSourceCurrency" :locale="currentLocale" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('garage.journal.payee')
          }}</span>
          <input
            v-model="payee"
            type="text"
            :placeholder="t('garage.journal.payeePlaceholder')"
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
            @input="payeeTouched = true"
          />
        </label>
      </div>

      <div v-else-if="journalMode === 'LINK'" class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.journal.entryLabel')
        }}</span>
        <SearchableSelect
          v-model="entryId"
          :options="entryOptions"
          :trigger-label="t('garage.journal.selectEntry')"
          :search-label="t('garage.journal.selectEntry')"
          :empty-text="t('garage.journal.noEntryFound')"
        />
      </div>
    </section>

    <StatusTag v-if="combinedError" tone="danger">{{ combinedError }}</StatusTag>

    <div class="flex gap-2">
      <ActionButton type="submit" :disabled="isSubmitting">
        {{ isSubmitting ? t('garage.form.saving') : t('garage.form.save') }}
      </ActionButton>
      <button
        type="button"
        class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
        @click="$emit('cancel')"
      >
        {{ t('garage.form.cancel') }}
      </button>
    </div>
  </form>
</template>
